import React, { useEffect, useState } from 'react';
import { 
  Container, 
  Typography, 
  Box, 
  Card, 
  useTheme, 
  alpha, 
  Avatar,
  Divider,
  Grid,
  Paper,
  IconButton,
  Chip
} from '@mui/material';
import { fetchRestaurantInfo } from './api';
import config from './config';
import { useTranslation } from 'react-i18next';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import StoreIcon from '@mui/icons-material/Store';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import EmailIcon from '@mui/icons-material/Email';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PhoneIcon from '@mui/icons-material/Phone';
import VisibilityIcon from '@mui/icons-material/Visibility';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import InfoIcon from '@mui/icons-material/Info';
import FacebookIcon from '@mui/icons-material/Facebook';
import InstagramIcon from '@mui/icons-material/Instagram';
import TwitterIcon from '@mui/icons-material/Twitter';

function AboutUs() {
  const theme = useTheme();
  const [info, setInfo] = useState(null);
  const [bgImage, setBgImage] = useState(null);
  const { t, i18n } = useTranslation();

  useEffect(() => {
    fetchRestaurantInfo().then(setInfo).catch(console.error);
    
    // Fetch slider images for background
    fetch(`${config.API_BASE_URL}/RestaurantInfo/slider`)
      .then(res => res.json())
      .then(data => {
        if (Array.isArray(data)) {
          const image = data.find(img => img.IsActive);
          if (image) {
            const imgPath = image.ImagePath.startsWith('http') 
              ? image.ImagePath 
              : `${config.SITE_BASE_URL}${image.ImagePath.startsWith('/') ? '' : '/'}${image.ImagePath}`;
            setBgImage(imgPath);
          }
        }
      })
      .catch(console.error);
  }, []);

  if (!info) {
    return (
      <Container sx={{ mt: 4, textAlign: 'center' }}>
        <Typography>{t('common.loading', 'Loading...')}</Typography>
      </Container>
    );
  }

  const logoUrl = info.LogoPath 
    ? (info.LogoPath.startsWith('http') ? info.LogoPath : `${config.SITE_BASE_URL}${info.LogoPath.startsWith('/') ? '' : '/'}${info.LogoPath}`)
    : null;

  // Parse additional phones
  let additionalPhones = [];
  try {
    const phones = info.AdditionalPhones || info.additionalPhones;
    if (phones) {
      additionalPhones = typeof phones === 'string' ? JSON.parse(phones) : phones;
    }
  } catch (e) {
    console.error("Error parsing additional phones", e);
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', pb: 8 }}>
      {/* Hero Section */}
      <Box sx={{ 
        position: 'relative', 
        height: { xs: 380, md: 450 }, 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        color: 'white',
        mb: { xs: -6, md: -12 },
        overflow: 'hidden'
      }}>
        <Box sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundImage: bgImage ? `url(${bgImage})` : `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          filter: 'blur(3px)',
          transform: 'scale(1.1)'
        }} />
        <Box sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: `linear-gradient(to bottom, ${alpha('#000', 0.4)} 0%, ${alpha('#000', 0.8)} 100%)`,
          zIndex: 1
        }} />
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 2, textAlign: 'center', mt: { xs: 6, md: 0 } }}>
          <Typography 
            variant="h2" 
            fontWeight="900" 
            sx={{ 
              mb: 2, 
              textShadow: '0 4px 20px rgba(0,0,0,0.5)',
              fontSize: { xs: '2rem', md: '3.5rem' }
            }}
          >
            {info.Name}
          </Typography>
          <Typography 
            variant="h6" 
            sx={{ 
              opacity: 0.95, 
              fontWeight: 400, 
              maxWidth: 700, 
              mx: 'auto',
              fontSize: { xs: '1rem', md: '1.25rem' },
              px: { xs: 2, md: 0 }
            }}
          >
            {t('about.tagline', 'Your Complete Shopping Destination')}
          </Typography>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 3 }}>
        {/* Logo Card */}
        <Card sx={{ 
          borderRadius: 4, 
          boxShadow: '0 20px 60px rgba(0,0,0,0.12)', 
          overflow: 'visible',
          bgcolor: 'background.paper',
          mb: 4
        }}>
          <Box sx={{ 
            mt: { xs: -6, md: -10 }, 
            display: 'flex', 
            justifyContent: 'center',
            mb: { xs: 2, md: 4 }
          }}>
            {logoUrl ? (
              <Box
                component="img"
                src={logoUrl}
                alt="Logo"
                sx={{
                  width: { xs: 110, md: 200 },
                  height: { xs: 110, md: 200 },
                  objectFit: 'contain',
                  bgcolor: 'white',
                  border: '8px solid',
                  borderColor: 'background.paper',
                  boxShadow: '0 12px 40px rgba(0,0,0,0.2)',
                  borderRadius: '50%',
                  p: 2
                }}
              />
            ) : (
              <Avatar 
                sx={{ 
                  width: { xs: 110, md: 200 }, 
                  height: { xs: 110, md: 200 }, 
                  border: '8px solid',
                  borderColor: 'background.paper',
                  boxShadow: '0 12px 40px rgba(0,0,0,0.2)',
                  bgcolor: 'white'
                }}
              >
                <RestaurantIcon sx={{ fontSize: { xs: 55, md: 100 }, color: 'primary.main' }} />
              </Avatar>
            )}
          </Box>

          <Box sx={{ px: { xs: 3, md: 6 }, pb: { xs: 3, md: 5 } }}>
            {/* About Us Section */}
            {info.AboutUs && (
              <Box sx={{ mb: 6 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, justifyContent: 'center' }}>
                  <InfoIcon sx={{ fontSize: { xs: 28, md: 32 }, color: 'primary.main', mr: 1 }} />
                  <Typography variant="h4" fontWeight="800" sx={{ fontSize: { xs: '1.5rem', md: '2.125rem' } }}>
                    {t('about.aboutUs', 'من نحن')}
                  </Typography>
                </Box>
                <Typography 
                  variant="body1" 
                  sx={{ 
                    whiteSpace: 'pre-wrap',
                    lineHeight: 2, 
                    color: 'text.secondary', 
                    fontSize: { xs: '0.95rem', md: '1.1rem' },
                    textAlign: 'justify',
                    px: { xs: 0, md: 4 }
                  }}
                >
                  {info.AboutUs}
                </Typography>
              </Box>
            )}

            <Divider sx={{ my: 5 }} />

            {/* Our Vision Section */}
            {info.OurVision && (
              <Box sx={{ mb: 6 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, justifyContent: 'center' }}>
                  <VisibilityIcon sx={{ fontSize: { xs: 28, md: 32 }, color: 'secondary.main', mr: 1 }} />
                  <Typography variant="h4" fontWeight="800" sx={{ fontSize: { xs: '1.5rem', md: '2.125rem' } }}>
                    {t('about.ourVision', 'رؤيتنا')}
                  </Typography>
                </Box>
                <Paper 
                  elevation={0}
                  sx={{ 
                    bgcolor: alpha(theme.palette.secondary.main, 0.08),
                    p: { xs: 3, md: 4 },
                    borderRadius: 3,
                    borderRight: `6px solid ${theme.palette.secondary.main}`
                  }}
                >
                  <Typography 
                    variant="body1" 
                    sx={{ 
                      whiteSpace: 'pre-wrap',
                      lineHeight: 2, 
                      color: 'text.secondary', 
                      fontSize: { xs: '0.95rem', md: '1.1rem' },
                      textAlign: 'justify'
                    }}
                  >
                    {info.OurVision}
                  </Typography>
                </Paper>
              </Box>
            )}

            <Divider sx={{ my: 5 }} />

            {/* What We Provide Section */}
            {info.WhatWeProvide && (
              <Box sx={{ mb: 6 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, justifyContent: 'center' }}>
                  <LocalOfferIcon sx={{ fontSize: { xs: 28, md: 32 }, color: 'success.main', mr: 1 }} />
                  <Typography variant="h4" fontWeight="800" sx={{ fontSize: { xs: '1.5rem', md: '2.125rem' } }}>
                    {t('about.whatWeProvide', 'ما نقدمه')}
                  </Typography>
                </Box>
                <Paper 
                  elevation={0}
                  sx={{ 
                    bgcolor: alpha(theme.palette.success.main, 0.08),
                    p: { xs: 3, md: 4 },
                    borderRadius: 3,
                    borderRight: `6px solid ${theme.palette.success.main}`
                  }}
                >
                  <Typography 
                    variant="body1" 
                    sx={{ 
                      whiteSpace: 'pre-wrap',
                      lineHeight: 2, 
                      color: 'text.secondary', 
                      fontSize: { xs: '0.95rem', md: '1.1rem' },
                      textAlign: 'justify'
                    }}
                  >
                    {info.WhatWeProvide}
                  </Typography>
                </Paper>
              </Box>
            )}

            <Divider sx={{ my: 5 }} />

            {/* Why Us Section */}
            {info.WhyUs && (
              <Box sx={{ mb: 6 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, justifyContent: 'center' }}>
                  <ThumbUpIcon sx={{ fontSize: { xs: 28, md: 32 }, color: 'warning.main', mr: 1 }} />
                  <Typography variant="h4" fontWeight="800" sx={{ fontSize: { xs: '1.5rem', md: '2.125rem' } }}>
                    {t('about.whyUs', 'لماذا نحن')}
                  </Typography>
                </Box>
                <Paper 
                  elevation={0}
                  sx={{ 
                    bgcolor: alpha(theme.palette.warning.main, 0.08),
                    p: { xs: 3, md: 4 },
                    borderRadius: 3,
                    borderRight: `6px solid ${theme.palette.warning.main}`
                  }}
                >
                  <Typography 
                    variant="body1" 
                    sx={{ 
                      whiteSpace: 'pre-wrap',
                      lineHeight: 2, 
                      color: 'text.secondary', 
                      fontSize: { xs: '0.95rem', md: '1.1rem' },
                      textAlign: 'justify'
                    }}
                  >
                    {info.WhyUs}
                  </Typography>
                </Paper>
              </Box>
            )}
          </Box>
        </Card>

        {/* Contact Information Card */}
        <Card sx={{ 
          borderRadius: 4, 
          boxShadow: '0 10px 40px rgba(0,0,0,0.1)',
          bgcolor: 'background.paper',
          overflow: 'hidden',
          mb: 4
        }}>
          <Box sx={{ 
            background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
            p: { xs: 3, md: 4 },
            textAlign: 'center',
            color: 'white'
          }}>
            <Typography variant="h5" fontWeight="bold" gutterBottom sx={{ fontSize: { xs: '1.25rem', md: '1.5rem' } }}>
              {t('about.contact', 'تواصل معنا')}
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.9 }}>
              {t('about.contactSubtitle', 'نحن هنا لخدمتك')}
            </Typography>
          </Box>

          <Box sx={{ p: { xs: 3, md: 4 } }}>
            <Grid container spacing={3}>
              {/* Address */}
              {info.Address && (
                <Grid item xs={12} md={6}>
                  <Paper 
                    elevation={0} 
                    sx={{ 
                      p: 3, 
                      bgcolor: alpha(theme.palette.primary.main, 0.05),
                      borderRadius: 2,
                      height: '100%',
                      display: 'flex',
                      alignItems: 'center'
                    }}
                  >
                    <LocationOnIcon sx={{ fontSize: 40, color: 'primary.main', mr: 2, flexShrink: 0 }} />
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                        {t('about.address', 'العنوان')}
                      </Typography>
                      <Typography variant="body1" fontWeight="600" sx={{ fontSize: { xs: '0.9rem', md: '1rem' } }}>
                        {info.Address}
                      </Typography>
                    </Box>
                  </Paper>
                </Grid>
              )}

              {/* Email */}
              {info.Email && (
                <Grid item xs={12} md={6}>
                  <Paper 
                    elevation={0} 
                    sx={{ 
                      p: 3, 
                      bgcolor: alpha(theme.palette.error.main, 0.05),
                      borderRadius: 2,
                      height: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      cursor: 'pointer',
                      transition: 'all 0.3s',
                      '&:hover': { 
                        transform: 'translateY(-4px)',
                        boxShadow: '0 8px 24px rgba(0,0,0,0.1)'
                      }
                    }}
                    component="a"
                    href={`mailto:${info.Email}`}
                  >
                    <EmailIcon sx={{ fontSize: 40, color: 'error.main', mr: 2, flexShrink: 0 }} />
                    <Box sx={{ overflow: 'hidden' }}>
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                        {t('about.email', 'البريد الإلكتروني')}
                      </Typography>
                      <Typography 
                        variant="body1" 
                        fontWeight="600" 
                        sx={{ 
                          fontSize: { xs: '0.85rem', md: '1rem' },
                          overflow: 'hidden',
                          textOverflow: 'ellipsis'
                        }}
                      >
                        {info.Email}
                      </Typography>
                    </Box>
                  </Paper>
                </Grid>
              )}

              {/* Main Phone */}
              {info.PhoneNumber && (
                <Grid item xs={12} md={6}>
                  <Paper 
                    elevation={0} 
                    sx={{ 
                      p: 3, 
                      bgcolor: alpha('#25D366', 0.08),
                      borderRadius: 2,
                      height: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      cursor: 'pointer',
                      transition: 'all 0.3s',
                      '&:hover': { 
                        transform: 'translateY(-4px)',
                        boxShadow: '0 8px 24px rgba(37, 211, 102, 0.3)'
                      }
                    }}
                    onClick={() => window.open(`https://wa.me/${info.PhoneNumber.replace(/[^0-9]/g, '')}`, '_blank')}
                  >
                    <WhatsAppIcon sx={{ fontSize: 40, color: '#25D366', mr: 2, flexShrink: 0 }} />
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                        {t('about.whatsapp', 'واتساب')}
                      </Typography>
                      <Typography variant="body1" fontWeight="600" sx={{ fontSize: { xs: '0.9rem', md: '1rem' } }}>
                        {info.PhoneNumber}
                      </Typography>
                    </Box>
                  </Paper>
                </Grid>
              )}

              {/* Additional Phones */}
              {additionalPhones.length > 0 && additionalPhones.map((phone, index) => (
                <Grid item xs={12} md={6} key={index}>
                  <Paper 
                    elevation={0} 
                    sx={{ 
                      p: 3, 
                      bgcolor: alpha(theme.palette.info.main, 0.05),
                      borderRadius: 2,
                      height: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      cursor: 'pointer',
                      transition: 'all 0.3s',
                      '&:hover': { 
                        transform: 'translateY(-4px)',
                        boxShadow: '0 8px 24px rgba(0,0,0,0.1)'
                      }
                    }}
                    onClick={() => window.open(`https://wa.me/${phone.number.replace(/[^0-9]/g, '')}`, '_blank')}
                  >
                    <PhoneIcon sx={{ fontSize: 40, color: 'info.main', mr: 2, flexShrink: 0 }} />
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                        {phone.title}
                      </Typography>
                      <Typography variant="body1" fontWeight="600" sx={{ fontSize: { xs: '0.9rem', md: '1rem' } }}>
                        {phone.number}
                      </Typography>
                    </Box>
                  </Paper>
                </Grid>
              ))}
            </Grid>

            {/* Social Media */}
            {(info.FacebookUrl || info.InstagramUrl || info.TwitterUrl) && (
              <Box sx={{ mt: 4, textAlign: 'center' }}>
                <Divider sx={{ mb: 3 }}>
                  <Chip label={t('about.followUs', 'تابعنا')} />
                </Divider>
                <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, flexWrap: 'wrap' }}>
                  {info.FacebookUrl && (
                    <IconButton 
                      size="large"
                      sx={{ 
                        bgcolor: alpha('#1877F2', 0.1),
                        '&:hover': { bgcolor: '#1877F2', color: 'white' }
                      }}
                      onClick={() => window.open(info.FacebookUrl, '_blank')}
                    >
                      <FacebookIcon />
                    </IconButton>
                  )}
                  {info.InstagramUrl && (
                    <IconButton 
                      size="large"
                      sx={{ 
                        bgcolor: alpha('#E4405F', 0.1),
                        '&:hover': { 
                          background: 'linear-gradient(45deg, #F58529, #DD2A7B, #8134AF)',
                          color: 'white' 
                        }
                      }}
                      onClick={() => window.open(info.InstagramUrl, '_blank')}
                    >
                      <InstagramIcon />
                    </IconButton>
                  )}
                  {info.TwitterUrl && (
                    <IconButton 
                      size="large"
                      sx={{ 
                        bgcolor: alpha('#1DA1F2', 0.1),
                        '&:hover': { bgcolor: '#1DA1F2', color: 'white' }
                      }}
                      onClick={() => window.open(info.TwitterUrl, '_blank')}
                    >
                      <TwitterIcon />
                    </IconButton>
                  )}
                </Box>
              </Box>
            )}
          </Box>
        </Card>

        {/* Footer */}
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1, fontSize: { xs: '0.75rem', md: '0.875rem' } }}>
            © {new Date().getFullYear()} {info.Name}. {t('footer.allRightsReserved', 'جميع الحقوق محفوظة')}
          </Typography>
          <Typography variant="caption" color="text.disabled">
            v1.0.0
          </Typography>
        </Box>
      </Container>
    </Box>
  );
}

export default AboutUs;